import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, CATEGORIES } from "../lib/api";
import EventCard from "../components/EventCard";
import SpotlightCarousel from "../components/SpotlightCarousel";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ArrowRight, Search, Ticket, ShieldCheck } from "lucide-react";

const CATEGORY_ART = {
  Travel: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Music: "https://images.unsplash.com/photo-1570872626485-d8ffea69f463?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Nightlife: "https://images.unsplash.com/photo-1578736641330-3155e606cd40?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Comedy: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Sports: "https://images.unsplash.com/photo-1599158150601-1417ebbaafdd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Theatre: "https://images.unsplash.com/photo-1503095396549-807759245b35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Workshops: "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const MARQUEE = ["Concerts", "Warehouse raves", "Standup", "Derbies", "Theatre", "Workshops", "Secret gigs", "Sunrise sets"];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [orgOpen, setOrgOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/events/featured").then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/events${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <div>
      <section className="relative overflow-hidden noise">
        <div className="hex-grid-bg absolute inset-0" />
        <img src="https://images.unsplash.com/photo-1574155376612-bfa4ed8aabfd?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600" alt="" aria-hidden className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[60%] object-cover opacity-[0.14] grayscale lg:block" style={{ maskImage: "linear-gradient(to right, transparent, #000 35%, #000 70%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, #000 35%, #000 70%, transparent)" }} />
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-96 w-96 rounded-full bg-punch-500/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.2fr,1fr] lg:py-24">
          <div className="fade-in-up">
            <h1 className="font-display text-4xl font-extrabold leading-[0.92] tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">
              Where the city<br />
              <span className="text-outline">comes alive</span><br />
              <span className="text-blush">after</span><span className="text-rose">hours</span><span className="text-cream">.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400 sm:text-lg">
              Concerts, warehouse parties, standup nights, sold-out derbies — find your scene and lock your spot in seconds.
            </p>

            <form onSubmit={submitSearch} className="mt-8 flex max-w-lg items-center gap-2 rounded-full border border-zinc-800 bg-[#3A1020] p-1.5 transition focus-within:border-amber-500/60">
              <div className="flex flex-1 items-center gap-2 pl-4">
                <Search className="h-4 w-4 text-zinc-500" />
                <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events, artists, cities..." className="w-full bg-transparent py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-500" />
              </div>
              <Button data-testid="hero-search-btn" type="submit" className="rounded-full bg-amber-500 px-6 font-semibold text-zinc-950 hover:bg-amber-400">
                Explore <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </form>

            <div className="mt-8 flex flex-wrap gap-6 text-xs uppercase tracking-widest text-zinc-500">
              <div className="flex items-center gap-2"><Ticket className="h-4 w-4 text-amber-500" /><span className="font-mono">QR ticket in your inbox</span></div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /><span className="font-mono">PAN-verified organizers</span></div>
            </div>
          </div>

          <div className="fade-in-up lg:pl-6">
            <SpotlightCarousel events={featured} />
          </div>
        </div>

        <div className="relative border-y border-zinc-800/60 bg-[#33101C] py-3">
          <div className="flex w-max marquee gap-10 whitespace-nowrap font-display text-sm font-bold uppercase tracking-widest text-zinc-500">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="flex items-center gap-10">{m}<span className="text-punch-500">✦</span></span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-display text-2xl font-extrabold text-zinc-50 sm:text-3xl">Pick your scene</h2>
          <Link to="/events" className="text-sm font-mono uppercase tracking-widest text-amber-400 hover:text-amber-300">All events →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {CATEGORIES.map((c, i) => (
            <Link key={c} to={`/events?category=${c}`} data-testid={`category-tile-${c.toLowerCase()}`}
              className={`group relative aspect-square overflow-hidden rounded-2xl border border-zinc-800/80 transition hover:border-amber-500/40 ${i % 2 ? "lg:translate-y-4" : ""}`}>
              <img src={CATEGORY_ART[c]} alt={c} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B0B15] via-[#2B0B15]/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4"><div className="font-display text-base font-bold text-zinc-50">{c}</div></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-punch-400">Trending now</div>
            <h2 className="font-display text-2xl font-extrabold text-zinc-50 sm:text-3xl">On sale this week</h2>
          </div>
          <Link to="/events" className="text-sm font-mono uppercase tracking-widest text-amber-400 hover:text-amber-300">View all →</Link>
        </div>
        {events.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800 bg-[#3A1020] p-10 text-center text-sm text-zinc-500">No events yet. Check back soon.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {events.slice(0, 8).map((e) => <EventCard key={e.id} event={e} />)}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-[#3A1020] p-8 sm:p-12 noise">
          <div className="hex-grid-bg absolute inset-0" />
          <div className="relative flex flex-col items-start gap-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">For organizers</div>
            <h2 className="font-display text-3xl font-extrabold leading-tight text-zinc-50 sm:text-4xl">Got a crowd? <span className="text-rose">Host it afterhours.</span></h2>
            <Button data-testid="cta-become-organizer" onClick={() => setOrgOpen(true)} className="bg-amber-500 font-semibold text-zinc-950 hover:bg-amber-400">
              Become an organizer <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
              <DialogContent data-testid="organizer-details" className="border-zinc-800 bg-[#33101C] text-zinc-100 sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-extrabold">Host it <span className="text-rose">afterhours.</span></DialogTitle>
                  <DialogDescription className="text-zinc-300">Attendees pay the booking fee. You pay <span className="font-bold text-amber-400">0% commission</span> (just 1% tax) and get paid every Wednesday straight to your bank. Verify once with PAN + Instagram so fans know you're legit.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {[{ k: "Commission", v: "0%" }, { k: "Setup time", v: "< 3 min" }, { k: "Payout day", v: "Wed" }, { k: "Spotlight ad", v: "₹1,999" }].map((s) => (
                    <div key={s.k} className="rounded-2xl border border-zinc-800/80 bg-[#2B0B15] p-4">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</div>
                      <div className="font-display text-2xl font-extrabold text-amber-400">{s.v}</div>
                    </div>
                  ))}
                </div>
                <Button data-testid="cta-organizer-register" onClick={() => navigate("/register?role=organizer")} className="w-full bg-rose font-semibold text-white hover:bg-punch-600">
                  Create my organizer account <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
    </div>
  );
}
